<template>
  <div class="resource-requests-view">
    <b-card class="admins-list">
      <template slot="header">
        <div class="d-flex justify-content-between align-items-start flex-wrap w-100">
          <div class="mb-2 mb-md-0">
            <h3 class="mb-1 resource-requests-main-title">Resource Served - {{ resourceName }}</h3>
          </div>
          <ul class="list-inline header_table mb-0">
            <li class="list-inline-item place-center ml-10">
              <button
                type="button"
                aria-label="Export"
                aria-live="assertive"
                v-if="exportBtn"
                @click="exportData()"
                title="Download Resource Served data"
                class="btn custom-card-header-filter btn-dropbox tab-focus"
              ><i class="icon-cloud-download"></i></button>
              <button
                type="button"
                v-if="processBtn"
                v-b-tooltip.hover.bottom
                title="Exporting data"
                class="btn custom-card-header-filter btn-dropbox"
              ><i class="fa fa-spinner fa-spin"></i></button>
            </li>
          </ul>
        </div>
      </template>
      <b-card-body>
        <div
          v-if="loadingData"
          class="text-center py-5"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <i class="fa fa-spinner fa-spin fa-2x mb-3 d-block" aria-hidden="true"></i>
          <p class="mb-0 text-muted">Loading resource data…</p>
        </div>
        <template v-else>
        <search-section search-placeholder="Search Requests..." @search="handleSearch" @limit-change="handleLimitChange">
          <template #before-records>
            <b-form-group
              v-if="circles.length >= 1"
              label="Circle:"
              label-for="circleFilter"
              class="mb-0 limit-group mr-3"
            >
              <b-form-select
                id="circleFilter"
                v-model="selectedCircle"
                class="custom-limit-select"
              >
                <option value="all">All</option>
                <option v-for="circle in circles" :key="circle.short" :value="circle.short">{{ circle.name }}</option>
              </b-form-select>
            </b-form-group>
          </template>
        </search-section>
        <v-client-table
          ref="requestsTable"
          v-bind:data="filteredRequests"
          v-bind:columns="columns"
          :options="options"
          id="dataTable"
          class="resource-list resource-requests-table"
        >
          <span slot="date" slot-scope="props">
            <span
              v-if="props.row.date"
              class="display-link resource-served-link"
              role="link"
              tabindex="0"
              @click="goToRequest(props.row._id, true)"
              @keyup.enter="goToRequest(props.row._id, true)"
            >{{ props.row.date | momentDate }}<span class="resource-served-link-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.28" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg></span></span>
            <span v-else>N/A</span>
          </span>
          <span slot="member" slot-scope="props">
            <span
              v-if="props.row.memberId"
              class="display-link resource-served-link"
              role="link"
              tabindex="0"
              @click="goToMember(props.row.memberId)"
              @keyup.enter="goToMember(props.row.memberId)"
            >{{ props.row.member }}<span class="resource-served-link-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.28" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg></span></span>
            <span v-else>{{ props.row.member || 'N/A' }}</span>
          </span>
          <span slot="action" slot-scope="props">
            <b-dropdown variant="btn btn-dropbox btn-sm tab-focus" right>
              <template slot="button-content">
                <i class="icon-settings"></i>
              </template>
              <b-dropdown-item @click="goToRequest(props.row._id, true)">
                View Request
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </v-client-table>
        </template>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import VueNotifications from 'vue-notifications';
import { exportCSV } from '@/assets/js/common';
import SearchSection from '@/views/engagement/components/searchSection.vue';

export default {
  name: 'ResourceRequests',
  components: {
    SearchSection,
  },
  data() {
    return {
      loadingData: true,
      requests: [],
      circles: [],
      selectedCircle: 'all',
      shortToNameMap: {},
      resourceName: '',
      exportBtn: true,
      processBtn: false,
      token: localStorage.getItem('token'),
      columns: ['member', 'date', 'name', 'description', 'clicked', 'action'],
      options: {
        sortable: ['member', 'date', 'name', 'description', 'clicked'],
        orderBy: { column: 'date', ascending: false },
        sortIcon: { base: 'fa', up: 'fa-sort-asc', down: 'fa-sort-desc', is: 'fa-sort' },
        headings: {
          name: 'Subject',
          description: 'Question',
          date: 'Date',
          member: 'Member',
          clicked: 'Clicked',
          action: 'Action',
        },
        pagination: {
          show: true,
          dropdown: false,
          chunk: 10,
          edge: false,
          nav: 'scroll',
        },
        filterable: true,
        perPageDropdown: [],
      },
    };
  },
  computed: {
    filteredRequests() {
      if (this.selectedCircle === 'all') {
        return this.requests;
      }
      return this.requests.filter(r => r.circle === this.selectedCircle);
    },
  },
  notifications: {
    showDownloadingMsg: {
      type: VueNotifications.types.success,
      message: 'Export request sent. Data is now downloading.',
    },
    showWarnMsg: {
      type: VueNotifications.types.warn,
      message: 'No data found for export file',
    },
    showErrorMsg: {
      type: VueNotifications.types.error,
      message: 'There was an error while processing your request.',
    },
  },
  async created() {
    await Promise.all([this.loadCommunities(), this.loadData()]);
    this.buildCircles();
  },
  methods: {
    async loadCommunities() {
      try {
        const { data } = await this.axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/dashboard/sidebar-community`,
          { headers: { 'x-access-token': this.token } }
        );
        data.forEach(community => {
          if (community.additionalParams && community.additionalParams.length > 0) {
            community.additionalParams.forEach(param => {
              if (param && param.short && param.name) {
                this.shortToNameMap[param.short] = param.name;
              }
            });
          }
        });
      } catch (err) {
        console.error('Error loading communities:', err);
      }
    },
    async loadData() {
      this.loadingData = true;
      const resourceId = this.$route.params.resource_id;
      const headers = { headers: { 'x-access-token': this.token } };
      try {
        const { data } = await this.axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/dashboard/resource/${resourceId}/requests`,
          headers
        );
        if (data.requests) {
          this.requests = data.requests;
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          localStorage.removeItem('authUser');
          this.$router.push('/login');
        }
      }
      try {
        const { data } = await this.axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/dashboard/resource/${resourceId}`,
          headers
        );
        if (data.resource && data.resource.name) {
          this.resourceName = data.resource.name;
        }
      } catch {
        /* Resource title is optional*/
      } finally {
        this.loadingData = false;
      }
      this.$helpers.setTabindexOnTableCells();
    },
    buildCircles() {
      const seen = new Set();
      this.requests.forEach(r => {
        if (r.circle && !seen.has(r.circle)) {
          seen.add(r.circle);
          this.circles.push({
            short: r.circle,
            name: this.shortToNameMap[r.circle] || r.circle,
          });
        }
      });
      this.circles.sort((a, b) => a.name.localeCompare(b.name));
    },
    goToRequest(requestId, fromResourceServed) {
      const location = fromResourceServed
        ? {
            name: 'RequestDetail',
            params: { request_id: requestId },
            query: { fromResourceServed: '1' },
          }
        : {
            name: 'RequestDetail',
            params: { request_id: requestId },
          };
      this.$router.push(location).catch(() => {});
    },
    goToMember(memberId) {
      this.$router
        .push({ name: 'MemberDetails', params: { member_id: memberId } })
        .catch(() => {});
    },
    handleSearch(searchText) {
      this.$refs.requestsTable.setFilter(searchText);
    },
    handleLimitChange(newLimit) {
      this.$refs.requestsTable.setLimit(newLimit);
    },
    async exportData() {
      if (this.filteredRequests.length === 0) {
        this.showWarnMsg();
        return;
      }
      this.showDownloadingMsg();
      this.exportBtn = false;
      this.processBtn = true;
      const desiredOrder = ['member', 'date', 'name', 'description', 'clicked'];
      const exportData = this.filteredRequests.map(row => {
        const out = {};
        desiredOrder.forEach(key => {
          out[key] = row[key];
        });
        return out;
      });
      await exportCSV(exportData, 'resource-requests', []);
      this.processBtn = false;
      this.exportBtn = true;
    },
  },
};
</script>

<style>
.resource-requests-table th,
.resource-requests-table td {
  vertical-align: middle;
}

.resource-requests-table th:nth-child(1),
.resource-requests-table th:nth-child(2) {
  text-align: center !important;
}

.resource-requests-table td:nth-child(1),
.resource-requests-table td:nth-child(2) {
  text-align: left !important;
}

.resource-requests-table td:nth-child(1) *,
.resource-requests-table td:nth-child(2) * {
  text-align: left !important;
}

.resource-requests-table th:nth-child(4),
.resource-requests-table td:nth-child(4) {
  min-width: 130px;
  white-space: nowrap;
}

.resource-requests-table th:nth-child(5),
.resource-requests-table td:nth-child(5) {
  min-width: 125px;
}

.resource-requests-table th:nth-child(6),
.resource-requests-table td:nth-child(6) {
  min-width: 120px;
  white-space: nowrap;
}

.resource-served-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.resource-served-link-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 0;
}

.resource-served-link-icon svg {
  display: block;
  opacity: 0.88;
}

</style>
